import { useEffect, useRef, useState, type ChangeEvent, type DragEvent, type FormEvent, type HTMLInputTypeAttribute } from 'react';

import { ErrorState } from '../../components/ui/Feedback';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { ApiError } from '../../lib/api-client';
import { isPhoneFieldName, normalizePhoneInput, PHONE_INPUT_MAX_LENGTH, PHONE_INPUT_PATTERN, PHONE_INPUT_TITLE } from '../../lib/phone-input';
import { useToast } from '../../lib/toast';
import {
  buildGeneralSettingsPayload,
  indianStateOptions,
  isGeneralSettingsImageReading,
  makeGeneralSettingsFormValues,
  readGeneralSettingsImage,
  validateGeneralSettingsForm,
  type GeneralSettingsFormErrors,
  type GeneralSettingsFormValues,
  type GeneralSettingsImageReadingState,
} from './general-settings.form';
import { useGeneralSettings, useSaveGeneralSettings } from './settings.queries';

const errorMessage = (error: unknown, fallback: string) => error instanceof ApiError ? error.message : fallback;

const fieldId = (field: keyof GeneralSettingsFormValues) =>
  `general-${field.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;

interface GeneralFieldProps {
  field: keyof GeneralSettingsFormValues;
  label: string;
  value: string;
  error?: string | undefined;
  required?: boolean;
  type?: HTMLInputTypeAttribute;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
  autoComplete?: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

const GeneralField = ({
  field,
  label,
  value,
  error,
  required = false,
  type = 'text',
  min,
  max,
  step,
  className = '',
  autoComplete,
  placeholder,
  onChange,
}: GeneralFieldProps) => {
  const id = fieldId(field);
  const phoneField = isPhoneFieldName(field);
  return (
    <label className={`general-settings-field ${className}`.trim()} htmlFor={id}>
      <span>{label}{required ? <b aria-hidden="true"> *</b> : null}</span>
      <input
        id={id}
        name={field}
        type={phoneField ? 'tel' : type}
        value={value}
        min={min}
        max={max}
        step={step}
        maxLength={phoneField ? PHONE_INPUT_MAX_LENGTH : undefined}
        inputMode={phoneField ? 'numeric' : undefined}
        pattern={phoneField ? PHONE_INPUT_PATTERN : undefined}
        title={phoneField ? PHONE_INPUT_TITLE : undefined}
        required={required}
        autoComplete={autoComplete}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(phoneField ? normalizePhoneInput(event.target.value) : event.target.value)}
      />
      {error ? <small className="general-settings-field-error" id={`${id}-error`}>{error}</small> : null}
    </label>
  );
};

interface GeneralImageFieldProps {
  field: 'logoDataUrl' | 'smallLogoDataUrl' | 'letterHeadImageDataUrl';
  label: string;
  guidance: string;
  value: string;
  error?: string | undefined;
  small?: boolean;
  reading: boolean;
  onChange: (value: string) => void;
  onError: (message?: string) => void;
  onReadingChange: (reading: boolean) => void;
}

const GeneralImageField = ({
  field,
  label,
  guidance,
  value,
  error,
  small = false,
  reading,
  onChange,
  onError,
  onReadingChange,
}: GeneralImageFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const readGenerationRef = useRef(0);
  const id = fieldId(field);
  const canPreview = /^data:image\/(?:jpeg|png|webp);base64,/i.test(value);

  const chooseImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;
    const generation = readGenerationRef.current + 1;
    readGenerationRef.current = generation;
    onReadingChange(true);
    onError();
    try {
      const image = await readGeneralSettingsImage(file);
      if (readGenerationRef.current === generation) onChange(image);
    } catch (readError) {
      if (readGenerationRef.current === generation) {
        onError(readError instanceof Error ? readError.message : 'The image could not be read.');
      }
    } finally {
      if (readGenerationRef.current === generation) {
        onReadingChange(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    }
  };

  const clearImage = () => {
    readGenerationRef.current += 1;
    onReadingChange(false);
    onChange('');
    onError();
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="general-settings-field general-settings-image-field general-span-3">
      <label htmlFor={id}>{label}</label>
      <input
        ref={inputRef}
        id={id}
        name={field}
        type="file"
        accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
        disabled={reading}
        aria-invalid={Boolean(error)}
        aria-describedby={`${id}-guidance${error ? ` ${id}-error` : ''}`}
        onChange={(event) => void chooseImage(event)}
      />
      <small className="general-settings-field-hint" id={`${id}-guidance`}>
        {guidance} - PNG, JPEG or WebP
      </small>
      {reading ? <small className="general-settings-image-reading" role="status">Reading image...</small> : null}
      {error ? <small className="general-settings-field-error" id={`${id}-error`}>{error}</small> : null}
      {canPreview ? (
        <div className={`general-settings-image-preview${small ? ' small' : ' logo'}`}>
          <img src={value} alt={`${label} preview`} />
          <button type="button" onClick={clearImage} disabled={reading}>Clear</button>
        </div>
      ) : value ? (
        <div className="general-settings-image-invalid">
          <span>The saved image cannot be previewed.</span>
          <button type="button" onClick={clearImage} disabled={reading}>Clear</button>
        </div>
      ) : null}
    </div>
  );
};

interface GeneralDropzoneImageFieldProps extends Omit<GeneralImageFieldProps, 'small'> {
  previewLabel: string;
}

const GeneralDropzoneImageField = ({
  field,
  label,
  guidance,
  previewLabel,
  value,
  error,
  reading,
  onChange,
  onError,
  onReadingChange,
}: GeneralDropzoneImageFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const readGenerationRef = useRef(0);
  const [dragging, setDragging] = useState(false);
  const id = fieldId(field);
  const canPreview = /^data:image\/(?:jpeg|png|webp);base64,/i.test(value);

  const readFile = async (file: File) => {
    const generation = readGenerationRef.current + 1;
    readGenerationRef.current = generation;
    onReadingChange(true);
    onError();
    try {
      const image = await readGeneralSettingsImage(file);
      if (readGenerationRef.current === generation) onChange(image);
    } catch (readError) {
      if (readGenerationRef.current === generation) {
        onError(readError instanceof Error ? readError.message : 'The image could not be read.');
      }
    } finally {
      if (readGenerationRef.current === generation) {
        onReadingChange(false);
        if (inputRef.current) inputRef.current.value = '';
      }
    }
  };

  const chooseImage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) void readFile(file);
  };
  const dropImage = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void readFile(file);
  };
  const clearImage = () => {
    readGenerationRef.current += 1;
    onReadingChange(false);
    onChange('');
    onError();
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="general-settings-field general-settings-letterhead-field general-span-6">
      <span>{label}</span>
      <label
        className={`general-settings-dropzone${dragging ? ' is-dragging' : ''}${error ? ' has-error' : ''}`.trim()}
        htmlFor={id}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
        onDrop={dropImage}
      >
        <input
          ref={inputRef}
          id={id}
          name={field}
          type="file"
          accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
          disabled={reading}
          aria-invalid={Boolean(error)}
          aria-describedby={`${id}-guidance${error ? ` ${id}-error` : ''}`}
          onChange={chooseImage}
        />
        <span className="general-settings-dropzone-icon" aria-hidden="true">+</span>
        <span className="general-settings-dropzone-copy">
          <strong>{value ? 'Replace letter head image' : 'Drop letter head image here'}</strong>
          <small id={`${id}-guidance`}>{guidance} - PNG, JPEG or WebP</small>
        </span>
        <span className="general-settings-dropzone-action">Browse</span>
      </label>
      {reading ? <small className="general-settings-image-reading" role="status">Reading image...</small> : null}
      {error ? <small className="general-settings-field-error" id={`${id}-error`}>{error}</small> : null}
      {canPreview ? (
        <div className="general-settings-letterhead-preview">
          <div><img src={value} alt={previewLabel} /></div>
          <button type="button" onClick={clearImage} disabled={reading}>Clear</button>
        </div>
      ) : value ? (
        <div className="general-settings-image-invalid">
          <span>The saved image cannot be previewed.</span>
          <button type="button" onClick={clearImage} disabled={reading}>Clear</button>
        </div>
      ) : null}
    </div>
  );
};

const SettingsLoading = () => <DetailSkeleton label="Loading settings" />;

export const GeneralSettingsPanel = () => {
  const { showToast } = useToast();
  const query = useGeneralSettings();
  const saveMutation = useSaveGeneralSettings();
  const [values, setValues] = useState<GeneralSettingsFormValues>(() => makeGeneralSettingsFormValues(query.data));
  const [errors, setErrors] = useState<GeneralSettingsFormErrors>({});
  const [imageReading, setImageReading] = useState<GeneralSettingsImageReadingState>({ logoDataUrl: false, smallLogoDataUrl: false, letterHeadImageDataUrl: false });
  const [confirmingSave, setConfirmingSave] = useState(false);
  const isReadingImage = isGeneralSettingsImageReading(imageReading);

  useEffect(() => {
    if (query.isSuccess) setValues(makeGeneralSettingsFormValues(query.data));
  }, [query.data, query.isSuccess]);

  if (query.isLoading) return <SettingsLoading />;
  if (query.isError) {
    return (
      <ErrorState
        title="Unable to load general settings"
        message={errorMessage(query.error, 'The hospital configuration could not be loaded.')}
        action={{ label: 'Try again', onClick: () => void query.refetch() }}
      />
    );
  }

  const updateValue = (field: keyof GeneralSettingsFormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => current[field] ? { ...current, [field]: undefined } : current);
  };

  const updateImageError = (field: 'logoDataUrl' | 'smallLogoDataUrl' | 'letterHeadImageDataUrl', message?: string) => {
    setErrors((current) => ({ ...current, [field]: message }));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isReadingImage) return;
    const nextErrors = validateGeneralSettingsForm(values);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      const firstField = Object.keys(nextErrors)[0] as keyof GeneralSettingsFormValues | undefined;
      if (firstField) document.getElementById(fieldId(firstField))?.focus();
      return;
    }
    setConfirmingSave(true);
  };

  const confirmSave = async () => {
    try {
      const record = await saveMutation.mutateAsync(buildGeneralSettingsPayload(values));
      setValues(makeGeneralSettingsFormValues(record));
      showToast('General settings saved successfully.', 'success');
    } catch (error) {
      showToast(errorMessage(error, 'General settings could not be saved.'), 'error');
    } finally {
      setConfirmingSave(false);
    }
  };

  const currentStateIsListed = indianStateOptions.some((option) => option.value === values.state);
  const stateOptions = currentStateIsListed || !values.state
    ? indianStateOptions
    : [{ value: values.state, label: values.state }, ...indianStateOptions];

  return (
    <div className="general-settings-card">
      <header className="general-settings-titlebar">
        <h2>General Settings</h2>
      </header>

      <form className="general-settings-form" onSubmit={(event) => void submit(event)} noValidate>
        <div className="general-settings-grid">
          <GeneralField
            field="title"
            label="Title"
            value={values.title}
            error={errors.title}
            required
            autoComplete="organization"
            className="general-span-3"
            onChange={(value) => updateValue('title', value)}
          />
          <GeneralField
            field="email"
            label="Email"
            type="email"
            value={values.email}
            error={errors.email}
            required
            autoComplete="email"
            className="general-span-3"
            onChange={(value) => updateValue('email', value)}
          />
          <GeneralField
            field="address"
            label="Address"
            value={values.address}
            error={errors.address}
            required
            autoComplete="street-address"
            className="general-span-2"
            onChange={(value) => updateValue('address', value)}
          />
          <label className="general-settings-field general-span-2" htmlFor={fieldId('state')}>
            <span>State<b aria-hidden="true"> *</b></span>
            <SearchableSelect
              id={fieldId('state')}
              options={stateOptions}
              value={values.state}
              placeholder="Select state"
              ariaInvalid={Boolean(errors.state)}
              ariaDescribedBy={errors.state ? `${fieldId('state')}-error` : undefined}
              onChange={(value) => updateValue('state', value)}
            />
            {errors.state ? <small className="general-settings-field-error" id={`${fieldId('state')}-error`}>{errors.state}</small> : null}
          </label>
          <GeneralField
            field="hfId"
            label="HF ID"
            value={values.hfId}
            error={errors.hfId}
            className="general-span-2"
            onChange={(value) => updateValue('hfId', value)}
          />
          <GeneralField
            field="hmisId"
            label="HMIS ID"
            value={values.hmisId}
            error={errors.hmisId}
            className="general-span-2"
            onChange={(value) => updateValue('hmisId', value)}
          />
          <GeneralField
            field="phone"
            label="Phone No."
            type="tel"
            value={values.phone}
            error={errors.phone}
            required
            autoComplete="tel"
            className="general-span-2"
            onChange={(value) => updateValue('phone', value)}
          />
          <GeneralField
            field="tdsPercent"
            label="TDS %"
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={values.tdsPercent}
            error={errors.tdsPercent}
            className="general-span-2"
            onChange={(value) => updateValue('tdsPercent', value)}
          />

          <GeneralImageField
            field="logoDataUrl"
            label="Logo"
            guidance="Expected size 245 x 48 px"
            value={values.logoDataUrl}
            error={errors.logoDataUrl}
            reading={imageReading.logoDataUrl}
            onChange={(value) => updateValue('logoDataUrl', value)}
            onError={(message) => updateImageError('logoDataUrl', message)}
            onReadingChange={(reading) => setImageReading((current) => ({ ...current, logoDataUrl: reading }))}
          />
          <GeneralImageField
            field="smallLogoDataUrl"
            label="Small Logo"
            guidance="Expected size 70 x 70 px"
            value={values.smallLogoDataUrl}
            error={errors.smallLogoDataUrl}
            small
            reading={imageReading.smallLogoDataUrl}
            onChange={(value) => updateValue('smallLogoDataUrl', value)}
            onError={(message) => updateImageError('smallLogoDataUrl', message)}
            onReadingChange={(reading) => setImageReading((current) => ({ ...current, smallLogoDataUrl: reading }))}
          />
          <GeneralDropzoneImageField
            field="letterHeadImageDataUrl"
            label="Letter Head Image"
            guidance="Single wide header image for print letter heads"
            previewLabel="Letter head preview"
            value={values.letterHeadImageDataUrl}
            error={errors.letterHeadImageDataUrl}
            reading={imageReading.letterHeadImageDataUrl}
            onChange={(value) => updateValue('letterHeadImageDataUrl', value)}
            onError={(message) => updateImageError('letterHeadImageDataUrl', message)}
            onReadingChange={(reading) => setImageReading((current) => ({ ...current, letterHeadImageDataUrl: reading }))}
          />

          <GeneralField
            field="rateLimiterMaxRequests"
            label="Rate Limiter Count (per IP)"
            type="number"
            min={1}
            max={1_000_000}
            step={1}
            value={values.rateLimiterMaxRequests}
            error={errors.rateLimiterMaxRequests}
            className="general-span-3"
            onChange={(value) => updateValue('rateLimiterMaxRequests', value)}
          />
          <GeneralField
            field="gzipCompressionLevel"
            label="Gzip Compression Level"
            type="number"
            min={-1}
            max={9}
            step={1}
            value={values.gzipCompressionLevel}
            error={errors.gzipCompressionLevel}
            className="general-span-3"
            onChange={(value) => updateValue('gzipCompressionLevel', value)}
          />
        </div>

        <p className="general-settings-restart-note">Rate limiter and gzip changes require a backend restart to take effect.</p>

        <footer className="general-settings-actions">
          <button className="button general-settings-save" type="submit" disabled={saveMutation.isPending || isReadingImage}>
            <span aria-hidden="true">&#10003;</span>
            {isReadingImage ? 'Reading image...' : saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </footer>
      </form>

      <ConfirmDialog
        open={confirmingSave}
        title="Save General Settings"
        description="Are you sure you want to save the changes to the hospital configuration?"
        confirmLabel="Save Changes"
        pendingLabel="Saving..."
        pending={saveMutation.isPending}
        onConfirm={() => void confirmSave()}
        onCancel={() => setConfirmingSave(false)}
      />
    </div>
  );
};
